import React from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { ownerApi } from "../../../../api/owner";
import { formatApiError } from "../../../../api/client";
import { compactActionSx } from "./ownerCenterUi";
import { OwnerVoiceFormControls, useOwnerVoiceForm, useOwnerVoiceOperator, voiceFieldProps } from "../voice/OwnerVoiceOperatorContext";

function todayDate() {
  const now = new Date();
  const pad = (value) => String(value).padStart(2, "0");
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

function emptyForm() {
  return {
    name: "",
    direction: "expense",
    amountMode: "fixed",
    fixedAmount: "",
    estimatedAmount: "",
    currency: "ARS",
    defaultAccountId: "",
    area: "Personal",
    category: "",
    dueDay: String(new Date().getDate()),
    startDate: todayDate(),
    endDate: "",
    notes: "",
    clientId: "",
    serviceId: "",
    quickServiceName: "",
  };
}

export default function OwnerRecurringFinanceDialog({ open, onClose, accounts = [], onSaved, preferredClient = null }) {
  const voice = useOwnerVoiceOperator();
  const [form, setForm] = React.useState(emptyForm);
  const [clients, setClients] = React.useState([]);
  const [services, setServices] = React.useState([]);
  const [loadingRelations, setLoadingRelations] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState("");

  React.useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    const preferredId = preferredClient?.id || preferredClient?._id || "";
    setForm({
      ...emptyForm(),
      ...(preferredId ? {
        direction: "income",
        clientId: preferredId,
        area: "Freelancer",
        category: "Servicio mensual",
      } : {}),
    });
    setError("");
    setLoadingRelations(true);
    Promise.all([
      ownerApi.listClients({ limit: 250 }),
      ownerApi.listServices({ includeInactive: false }),
    ]).then(([clientResponse, serviceResponse]) => {
      if (cancelled) return;
      setClients(clientResponse?.clients || []);
      setServices((serviceResponse?.services || []).filter((row) => row.active !== false));
    }).catch(() => {
      if (!cancelled) setError("No pude cargar clientes/servicios. El mensual financiero sigue disponible.");
    }).finally(() => {
      if (!cancelled) setLoadingRelations(false);
    });
    return () => { cancelled = true; };
  }, [open, preferredClient]);

  const eligibleAccounts = accounts.filter((account) => !account.archived && account.currency === form.currency);
  const selectedService = services.find((row) => String(row.id || row._id) === String(form.serviceId));
  const selectedClient = clients.find((row) => String(row.id || row._id) === String(form.clientId)) || preferredClient;

  const changeService = (serviceId) => {
    if (serviceId === "__quick__") {
      setForm((current) => ({ ...current, serviceId, quickServiceName: "", name: current.name }));
      return;
    }
    const service = services.find((row) => String(row.id || row._id) === String(serviceId));
    setForm((current) => ({
      ...current,
      serviceId,
      quickServiceName: "",
      name: service ? `${service.name}${selectedClient?.name ? ` · ${selectedClient.name}` : ""}` : current.name,
      currency: service?.currency || current.currency,
      fixedAmount: service?.basePrice ? String(service.basePrice) : current.fixedAmount,
      amountMode: "fixed",
      area: "Freelancer",
      category: service?.category || "Servicio mensual",
      defaultAccountId: "",
    }));
  };

  const submit = async () => {
    setSaving(true);
    setError("");
    try {
      let serviceId = form.direction === "income" ? form.serviceId : "";
      let service = selectedService;
      if (form.direction === "income" && serviceId === "__quick__") {
        if (!form.quickServiceName.trim()) throw new Error("Decime cómo se llama el servicio mensual.");
        const baseAmount = form.amountMode === "fixed" ? Number(form.fixedAmount || 0) : Number(form.estimatedAmount || 0);
        const created = await ownerApi.createService({
          name: form.quickServiceName.trim(),
          description: "Creado desde un acuerdo mensual Owner.",
          category: form.category || "Freelancer",
          billingMode: "monthly",
          currency: form.currency,
          basePrice: Number.isFinite(baseAmount) ? baseAmount : 0,
          estimatedCost: 0,
          active: true,
        });
        service = created?.service || null;
        serviceId = service?.id || service?._id || "";
      }

      const name = form.name.trim()
        || (service?.name ? `${service.name}${selectedClient?.name ? ` · ${selectedClient.name}` : ""}` : "")
        || (form.direction === "income" ? "Cobro mensual" : "Pago mensual");

      const response = await ownerApi.createRecurringFinance({
        ...form,
        name,
        dueDay: Number(form.dueDay),
        fixedAmount: form.amountMode === "fixed" ? Number(form.fixedAmount) : null,
        estimatedAmount: form.amountMode === "variable" && form.estimatedAmount ? Number(form.estimatedAmount) : null,
        defaultAccountId: form.defaultAccountId || null,
        endDate: form.endDate || null,
        clientId: form.direction === "income" ? (form.clientId || null) : null,
        serviceId: form.direction === "income" ? (serviceId || null) : null,
      });
      await onSaved?.(response?.plan || null);
      onClose?.();
      return response?.plan || true;
    } catch (err) {
      setError(formatApiError(err, err?.message || "No pude crear el mensual."));
      return false;
    } finally {
      setSaving(false);
    }
  };

  useOwnerVoiceForm({
    id: "recurring",
    open,
    config: {
      title: form.direction === "income" ? "Cobro mensual" : "Pago mensual",
      values: form,
      setValue: (name, value) => {
        if (name === "serviceId") {
          const raw = String(value || "");
          if (raw.startsWith("__quick__:")) {
            const quickName = raw.slice("__quick__:".length).trim();
            setForm((current) => ({
              ...current,
              serviceId: "__quick__",
              quickServiceName: quickName,
              name: quickName ? `${quickName}${selectedClient?.name ? ` · ${selectedClient.name}` : ""}` : current.name,
              area: "Freelancer",
              category: "Servicio mensual",
            }));
            return;
          }
          return changeService(value);
        }
        setForm((current) => ({
          ...current,
          [name]: value,
          ...(name === "direction" && value === "income" ? { area: "Freelancer", category: "Servicio mensual" } : {}),
          ...(name === "currency" ? { defaultAccountId: "" } : {}),
        }));
      },
      onSubmit: submit,
      onCancel: onClose,
      successMessage: "Mensual guardado.",
      afterSubmit: (_result, previousContext) => selectedClient ? {
        context: previousContext || { type: "person", entity: selectedClient },
        prompt: `Mensual guardado para ${selectedClient.name}. ¿Seguimiento, propuesta, proyecto, nota u otra acción?`,
      } : null,
      fields: [
        { name: "direction", label: "tipo", prompt: "¿Es un pago mensual o un cobro mensual?", kind: "choice", required: true, options: [
          { value: "expense", label: "Pago", aliases: ["gasto", "pago mensual"] },
          { value: "income", label: "Cobro", aliases: ["ingreso", "venta mensual", "servicio mensual", "cobro mensual"] },
        ] },
        { name: "clientId", label: "cliente", prompt: "¿A qué persona o cliente corresponde?", kind: "choice", required: true, speakOptions: false, when: (values) => values.direction === "income", skipIfFilled: true, options: clients.map((row) => ({ value: row.id || row._id, label: row.name, aliases: [row.name, row.company].filter(Boolean) })) },
        { name: "serviceId", label: "servicio", prompt: "¿Qué servicio mensual le vendés? Si no existe, simplemente decime su nombre y lo creo.", kind: "choice", required: true, speakOptions: false, when: (values) => values.direction === "income", parse: (raw) => {
          const normalized = String(raw || "").trim().toLowerCase();
          const existing = services.find((row) => {
            const name = String(row.name || "").trim().toLowerCase();
            return name && (normalized === name || normalized.includes(name));
          });
          if (existing) return existing.id || existing._id;
          const cleaned = String(raw || "").replace(/^(crear|nuevo|nueva)\s+servicio\s*/i, "").replace(/^servicio\s+/i, "").trim();
          return cleaned ? `__quick__:${cleaned}` : "__quick__";
        }, options: [
          { value: "__quick__", label: "Crear servicio", aliases: ["crear servicio", "nuevo servicio", "servicio nuevo"] },
          ...services.map((row) => ({ value: row.id || row._id, label: row.name, aliases: [row.name] })),
        ] },
        { name: "quickServiceName", label: "nombre del servicio", prompt: "¿Cómo se llama el servicio?", kind: "text", required: (values) => values.serviceId === "__quick__", skipIfFilled: true, when: (values) => values.direction === "income" && values.serviceId === "__quick__" },
        { name: "name", label: "nombre del mensual", aliases: ["nombre"], prompt: "¿Cómo querés reconocer este mensual? Decí siguiente para usar cliente y servicio.", kind: "text", skipIfFilled: true },
        { name: "amountMode", label: "monto", prompt: "¿El monto es fijo o variable?", kind: "choice", required: true, options: [{ value: "fixed", label: "Fijo" }, { value: "variable", label: "Variable" }] },
        { name: "currency", label: "moneda", prompt: "¿Pesos argentinos o dólares?", kind: "choice", required: true, options: [{ value: "ARS", label: "Pesos", aliases: ["ars", "pesos"] }, { value: "USD", label: "Dólares", aliases: ["usd", "dolares"] }] },
        { name: "fixedAmount", label: "importe mensual", aliases: ["monto", "precio"], prompt: "¿Cuánto es por mes?", kind: "number", required: (values) => values.amountMode === "fixed", when: (values) => values.amountMode === "fixed" },
        { name: "estimatedAmount", label: "importe estimado", aliases: ["monto estimado"], prompt: "¿Qué monto estimado uso hasta tener historial?", kind: "number", when: (values) => values.amountMode === "variable" },
        { name: "dueDay", label: "día de cobro", aliases: ["dia", "vencimiento"], prompt: "¿Qué día del mes ocurre normalmente?", kind: "number", required: true },
        { name: "defaultAccountId", label: "cuenta", prompt: "¿En qué cuenta suele impactar? Decí siguiente para elegir al registrar.", kind: "choice", speakOptions: false, options: [{ value: "", label: "Elegir al registrar", aliases: ["despues", "ninguna"] }, ...eligibleAccounts.map((account) => ({ value: account.id || account._id, label: account.name, aliases: [account.name] }))] },
        { name: "notes", label: "notas", prompt: "¿Alguna condición o contexto? Decí siguiente si no.", kind: "text" },
      ],
    },
  });

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ pb: .8 }}>
        <Typography component="div" sx={{ fontSize: "1.15rem", fontWeight: 1000, letterSpacing: "-.02em" }}>{form.direction === "income" ? "Cobro / servicio mensual" : "Nuevo mensual"}</Typography>
        <Typography sx={{ mt: .35, color: "text.secondary", fontWeight: 700, fontSize: ".86rem" }}>
          Puede ser un gasto recurrente o un servicio mensual vendido a una persona. El servicio vive en tu catálogo Owner y la proyección usa este mismo acuerdo.
        </Typography>
        <OwnerVoiceFormControls />
      </DialogTitle>
      <DialogContent dividers sx={{ pt: 2 }}>
        <Stack spacing={1.35}>
          {error ? <Alert severity="error">{error}</Alert> : null}
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.2}>
            <FormControl fullWidth><InputLabel>Movimiento</InputLabel><Select inputProps={voiceFieldProps("direction")} label="Movimiento" value={form.direction} onChange={(event) => setForm({ ...form, direction: event.target.value, ...(event.target.value === "income" ? { area: "Freelancer", category: "Servicio mensual" } : {}) })}><MenuItem value="expense">Pago / gasto mensual</MenuItem><MenuItem value="income">Cobro / servicio mensual</MenuItem></Select></FormControl>
            <FormControl fullWidth><InputLabel>Monto</InputLabel><Select inputProps={voiceFieldProps("amountMode")} label="Monto" value={form.amountMode} onChange={(event) => setForm({ ...form, amountMode: event.target.value })}><MenuItem value="fixed">Fijo</MenuItem><MenuItem value="variable">Variable</MenuItem></Select></FormControl>
            <FormControl sx={{ minWidth: 140 }}><InputLabel>Moneda</InputLabel><Select inputProps={voiceFieldProps("currency")} label="Moneda" value={form.currency} onChange={(event) => setForm({ ...form, currency: event.target.value, defaultAccountId: "" })}><MenuItem value="ARS">ARS</MenuItem><MenuItem value="USD">USD</MenuItem></Select></FormControl>
          </Stack>

          {form.direction === "income" ? (
            <Box sx={{ p: 1.35, borderRadius: 2.3, bgcolor: "rgba(243,90,58,.045)", border: "1px solid rgba(243,90,58,.12)" }}>
              <Typography sx={{ fontWeight: 950, mb: 1 }}>Relación comercial mensual</Typography>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.2}>
                <FormControl fullWidth disabled={loadingRelations}><InputLabel>Persona / cliente</InputLabel><Select inputProps={voiceFieldProps("clientId")} label="Persona / cliente" value={form.clientId} onChange={(event) => setForm({ ...form, clientId: event.target.value })}>{clients.map((client) => <MenuItem key={client.id || client._id} value={client.id || client._id}>{client.name}{client.company ? ` · ${client.company}` : ""}</MenuItem>)}</Select></FormControl>
                <FormControl fullWidth disabled={loadingRelations}><InputLabel>Servicio</InputLabel><Select inputProps={voiceFieldProps("serviceId")} label="Servicio" value={form.serviceId} onChange={(event) => changeService(event.target.value)}><MenuItem value="__quick__">+ Crear servicio mensual</MenuItem>{services.map((service) => <MenuItem key={service.id || service._id} value={service.id || service._id}>{service.name} · {service.currency}</MenuItem>)}</Select></FormControl>
              </Stack>
              {form.serviceId === "__quick__" ? <TextField inputProps={voiceFieldProps("quickServiceName")} sx={{ mt: 1.2 }} fullWidth label="Nombre del servicio" placeholder="Ej.: WebProfit" value={form.quickServiceName} onChange={(event) => setForm({ ...form, quickServiceName: event.target.value })} /> : null}
            </Box>
          ) : null}

          <TextField inputProps={voiceFieldProps("name")} fullWidth label="Cómo reconocer este mensual" placeholder={form.direction === "income" ? "Ej.: WebProfit · Cliente A" : "Ej.: Alquiler, Internet, prepaga"} value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} />
          {form.amountMode === "fixed" ? (
            <TextField inputProps={voiceFieldProps("fixedAmount")} fullWidth type="number" label={`Monto fijo mensual · ${form.currency}`} value={form.fixedAmount} onChange={(event) => setForm({ ...form, fixedAmount: event.target.value })} />
          ) : (
            <Box sx={{ p: 1.4, borderRadius: 2.3, bgcolor: "rgba(var(--wp-panel-ink-rgb,25,36,47),.035)", border: "1px solid rgba(var(--wp-panel-ink-rgb,25,36,47),.055)" }}>
              <TextField inputProps={voiceFieldProps("estimatedAmount")} fullWidth type="number" label={`Estimación inicial opcional · ${form.currency}`} value={form.estimatedAmount} onChange={(event) => setForm({ ...form, estimatedAmount: event.target.value })} />
              <Typography sx={{ mt: .7, color: "text.secondary", fontWeight: 700, fontSize: ".78rem" }}>Con pagos/cobros reales, la proyección usa el promedio de los últimos 6 meses.</Typography>
            </Box>
          )}
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.2}>
            <TextField fullWidth type="number" inputProps={{ ...voiceFieldProps("dueDay"), min: 1, max: 31 }} label="Día del mes" value={form.dueDay} onChange={(event) => setForm({ ...form, dueDay: event.target.value })} />
            <TextField fullWidth type="date" label="Empieza" InputLabelProps={{ shrink: true }} value={form.startDate} onChange={(event) => setForm({ ...form, startDate: event.target.value })} />
            <TextField fullWidth type="date" label="Termina (opcional)" InputLabelProps={{ shrink: true }} value={form.endDate} onChange={(event) => setForm({ ...form, endDate: event.target.value })} />
          </Stack>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.2}>
            <FormControl fullWidth><InputLabel>Cuenta habitual</InputLabel><Select inputProps={voiceFieldProps("defaultAccountId")} label="Cuenta habitual" value={form.defaultAccountId} onChange={(event) => setForm({ ...form, defaultAccountId: event.target.value })}><MenuItem value="">Elegir al registrar</MenuItem>{eligibleAccounts.map((account) => <MenuItem key={account.id || account._id} value={account.id || account._id}>{account.name} · {account.currency}</MenuItem>)}</Select></FormControl>
            <TextField fullWidth label="Área" value={form.area} onChange={(event) => setForm({ ...form, area: event.target.value })} />
            <TextField fullWidth label="Categoría" value={form.category} onChange={(event) => setForm({ ...form, category: event.target.value })} />
          </Stack>
          <TextField inputProps={voiceFieldProps("notes")} fullWidth multiline minRows={2} label="Notas" value={form.notes} onChange={(event) => setForm({ ...form, notes: event.target.value })} />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 2.5, py: 1.7 }}>
        <Button onClick={onClose} disabled={saving} sx={compactActionSx}>Cancelar</Button>
        <Button variant="contained" onClick={() => voice?.submitCurrentForm ? voice.submitCurrentForm() : submit()} disabled={saving || (form.amountMode === "fixed" && !(Number(form.fixedAmount) > 0)) || (form.direction === "income" && !form.clientId)} sx={{ ...compactActionSx, px: 2.3 }}>
          {saving ? "Guardando…" : form.direction === "income" ? "Crear mensual del cliente" : "Crear mensual"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
